/**
 * Derives a short "Key Findings" (TL;DR) summary for an article from its own
 * editorial content: the opening paragraph, the first substantive sentence
 * under each main H2, and — if needed — the first FAQ answer.
 *
 * The output is always 3-5 concise bullets so AI answer engines and skimming
 * readers get the core of the page without scrolling.
 */

export type TakeawaySection = {
  type: string;
  text?: string;
  items?: string[];
};

const MIN_BULLETS = 3;
const MAX_BULLETS = 5;
const MAX_CHARS = 210;

function sentences(text: string): string[] {
  return text
    .replace(/\s+/g, " ")
    .trim()
    .split(/(?<=[.!?])\s+(?=[A-Z0-9£$€"'])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function trim(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= MAX_CHARS) return clean;
  const cut = clean.slice(0, MAX_CHARS);
  const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("; "), cut.lastIndexOf(", "));
  return `${(stop > 120 ? cut.slice(0, stop) : cut).replace(/[,;:\s]+$/, "")}…`;
}

function label(heading: string): string {
  return heading.replace(/\s*[:–—-]\s*$/, "").replace(/\?$/, "").trim();
}

function usable(text: string | undefined): text is string {
  return Boolean(text && text.replace(/\s+/g, " ").trim().length >= 40);
}

export type KeyTakeawaysInput = {
  intro?: string;
  sections?: TakeawaySection[];
  faq?: { q: string; a: string }[];
  explicit?: string[];
};

export function buildKeyTakeaways({
  intro,
  sections = [],
  faq = [],
  explicit,
}: KeyTakeawaysInput): string[] {
  if (explicit?.length) return explicit.slice(0, MAX_BULLETS).map(trim);

  const bullets: string[] = [];
  const seen = new Set<string>();

  const push = (value: string) => {
    const text = trim(value);
    const key = text.slice(0, 60).toLowerCase();
    if (!text || seen.has(key) || bullets.length >= MAX_BULLETS) return;
    seen.add(key);
    bullets.push(text);
  };

  // 1. Opening promise of the article.
  if (usable(intro)) {
    const parts = sentences(intro!);
    push(parts.slice(0, parts[0] && parts[0].length < 90 ? 2 : 1).join(" "));
  }

  // 2. First substantive sentence (or list item) under each main H2.
  for (let i = 0; i < sections.length && bullets.length < MAX_BULLETS; i += 1) {
    const section = sections[i];
    if (section.type !== "h2" || !section.text) continue;

    for (let j = i + 1; j < sections.length; j += 1) {
      const next = sections[j];
      if (next.type === "h2") break;
      if (next.type === "p" && usable(next.text)) {
        push(`${label(section.text)}: ${sentences(next.text!)[0]}`);
        break;
      }
      if ((next.type === "ul" || next.type === "ol") && next.items?.length) {
        const item = next.items.find((it) => it.trim().length >= 30) ?? next.items[0];
        push(`${label(section.text)}: ${item}`);
        break;
      }
    }
  }

  // 3. Top-of-mind reader question, if we are still short.
  for (const item of faq) {
    if (bullets.length >= MIN_BULLETS) break;
    if (usable(item.a)) push(`${label(item.q)}: ${sentences(item.a)[0]}`);
  }

  return bullets.slice(0, MAX_BULLETS);
}
